'use client'

import { m } from 'framer-motion'
import {
  Award,
  CalendarDays,
  CircleCheck,
  Clock,
  Globe,
  Hammer,
  Handshake,
  Heart,
  Layers,
  type LucideIcon,
  Package,
  Percent,
  PhoneCall,
  Rocket,
  Ruler,
  ShieldCheck,
  Star,
  Target,
  TrendingUp,
  Truck,
  Users,
  Wallet,
  Zap,
} from 'lucide-react'

import AnimatedCounter from '@/components/AnimatedCounter'
import {
  type ImpactMetricIcon,
  impactPrefixForDisplay,
  impactSuffixForDisplay,
  normalizeImpactIcon,
} from '@/lib/cases/impactMetrics'

type Metric = {
  label: string
  value?: number
  text?: string
  prefix?: string
  suffix?: string
  precision?: number
  icon?: ImpactMetricIcon
}

export type CaseImpactBlock = {
  badge?: string
  title?: string
  description?: string
  metrics?: Metric[]
}

const ICONS: Record<ImpactMetricIcon, LucideIcon> = {
  trendingUp: TrendingUp,
  users: Users,
  zap: Zap,
  clock: Clock,
  calendar: CalendarDays,
  shield: ShieldCheck,
  star: Star,
  target: Target,
  award: Award,
  wallet: Wallet,
  percent: Percent,
  heart: Heart,
  handshake: Handshake,
  globe: Globe,
  layers: Layers,
  package: Package,
  ruler: Ruler,
  hammer: Hammer,
  truck: Truck,
  phone: PhoneCall,
  rocket: Rocket,
  check: CircleCheck,
}

function metricIcon(name?: Metric['icon']): LucideIcon {
  return ICONS[normalizeImpactIcon(name)]
}

const DEFAULT_METRICS: Metric[] = [
  { label: 'Рост трафика', value: 140, suffix: '%', icon: 'trendingUp' },
  { label: 'Удержание пользователей', value: 85, suffix: '%', icon: 'users' },
  { label: 'Скорость загрузки', value: 0.4, suffix: 's', icon: 'zap', precision: 1 },
  { label: 'Сокращение времени разработки', value: 40, suffix: '%', icon: 'clock' },
]

export default function CaseImpact({ impact }: { impact?: CaseImpactBlock | null }) {
  if (!impact) return null

  const metrics = impact.metrics && impact.metrics.length > 0 ? impact.metrics : DEFAULT_METRICS

  return (
    <section className="home-section relative overflow-hidden bg-transparent">
      <div className="home-container relative z-10">
        <div className="grid grid-cols-1 items-start gap-12 sm:gap-16 xl:grid-cols-12 xl:items-center xl:gap-24">
          <div className="min-w-0 xl:col-span-5">
            <m.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-6 w-fit rounded-2xl border border-white/10 bg-white/5 px-4 py-2"
            >
              <span className="home-meta text-blue-300">{impact.badge ?? 'Результат проекта'}</span>
            </m.div>

            <m.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="home-title mb-6 text-white sm:mb-8"
            >
              {impact.title ?? (
                <>
                  Измеримые{' '}
                  <span className="bg-gradient-to-r from-blue-300 to-purple-300 bg-clip-text text-transparent">
                    результаты
                  </span>
                </>
              )}
            </m.h2>

            <m.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="home-lead mb-6 text-gray-400 sm:mb-8"
            >
              {impact.description ??
                'Мы улучшаем производительность и метрики вовлеченности: быстрее загрузка, выше удержание и рост органического трафика относительно стартовых KPI.'}
            </m.p>
          </div>

          <div className="min-w-0 xl:col-span-7">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-7">
              {metrics.map((metric, i) => {
                const Icon = metricIcon(metric.icon)
                return (
                  <m.div
                    key={`metric-${i}`}
                    initial={{ opacity: 0, scale: 0.92 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                    className="group relative min-w-0 overflow-hidden rounded-[1.75rem] border border-white/10 bg-[linear-gradient(145deg,rgba(11,18,35,0.94)_0%,rgba(8,13,26,0.96)_100%)] p-6 shadow-[0_20px_70px_rgba(2,6,23,0.45)] backdrop-blur-xl transition-all duration-500 hover:-translate-y-0.5 hover:border-cyan-300/35 hover:shadow-[0_26px_90px_rgba(14,116,144,0.32)] sm:rounded-[2rem] sm:p-8 lg:p-10"
                  >
                    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_0%,rgba(56,189,248,0.18),transparent_58%)] opacity-70" />
                    <div className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-cyan-200/45 to-transparent" />
                    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_90%_85%,rgba(168,85,247,0.14),transparent_52%)] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

                    <div className="relative z-10 mb-6 flex items-start justify-between">
                      <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/15 bg-white/[0.03]">
                        <Icon className="h-6 w-6 text-gray-400 transition-colors group-hover:text-cyan-300" />
                      </span>
                      <div className="h-2 w-2 rounded-full bg-white/15 transition-colors group-hover:bg-cyan-300" />
                    </div>

                    <div className="relative z-10 flex items-baseline gap-1">
                      {metric.text ? (
                        <div className="min-w-0 !text-left">
                          <p className="mb-2 bg-gradient-to-b from-white to-gray-400 bg-clip-text text-4xl font-bold break-words text-transparent md:text-5xl">
                            {metric.text}
                          </p>
                        </div>
                      ) : (
                        <AnimatedCounter
                          value={metric.value ?? 0}
                          label=""
                          prefix={impactPrefixForDisplay(metric.prefix)}
                          suffix={impactSuffixForDisplay(metric.suffix)}
                          precision={metric.precision ?? 0}
                          className="!text-left"
                        />
                      )}
                    </div>
                    <p className="home-meta relative z-10 mt-2 tracking-[0.12em] break-words hyphens-auto text-gray-400 uppercase transition-colors group-hover:text-white">
                      {metric.label}
                    </p>
                  </m.div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
